import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  afterNextRender,
  computed,
  effect,
  forwardRef,
  input,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import type Quill from 'quill';
import type { Range } from 'quill';
import { applyRichTextCommand, findVariableKeys } from './rich-text-editor.commands';
import { RichTextCommand, RichTextFormats, RichTextVariable } from './rich-text-editor.models';
import { RichTextEditorToolbarComponent } from './toolbar/rich-text-editor-toolbar.component';

/**
 * Editor de texto rico com cara de documento (folha A4, barra de ferramentas completa), sobre o
 * **Quill 2** direto. Primeiro uso: Contrato de Parceria Comercial (card 3462). Guia no README.md
 * desta pasta.
 *
 * - `ControlValueAccessor`: funciona com `formControlName`, `[formControl]` e `ngModel`.
 * - Valor = HTML **nativo do Quill** (`root.innerHTML`, com classes `ql-*` e
 *   `<li data-list="...">`), e não o `getSemanticHTML()`: no Quill 2.0.3 o semântico troca todo
 *   espaço por `&nbsp;`, o que impede a quebra de linha no PDF e estraga o texto justificado.
 * - SSR: o Quill mexe em `document`. O import é dinâmico e só acontece no `afterNextRender`, que
 *   não roda no servidor.
 */
@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  imports: [CommonModule, RichTextEditorToolbarComponent],
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // O conteúdo do Quill é criado fora do Angular (sem os atributos do encapsulamento); os
  // seletores do SCSS são todos prefixados por `.app-rte` para não vazarem.
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
})
export class RichTextEditorComponent implements ControlValueAccessor, OnDestroy {
  /** Variáveis do menu "Variável" (inseridas como `{{key}}`). */
  readonly variables = input<RichTextVariable[]>([]);
  /** Somente leitura: esconde a barra e trava o texto. */
  readonly readonly = input(false);
  /** Altura mínima da folha (CSS). O padrão é a altura de uma página A4. */
  readonly minHeight = input('1123px');
  readonly placeholder = input('Comece a escrever...');

  /** Chaves das variáveis `{{...}}` encontradas no texto, a cada mudança. */
  readonly unfilledVariablesChange = output<string[]>();

  @ViewChild('editorHost', { static: true }) private editorHost!: ElementRef<HTMLDivElement>;

  readonly ready = signal(false);
  readonly fullscreen = signal(false);
  readonly formats = signal<RichTextFormats>({});
  private readonly disabled = signal(false);
  readonly isLocked = computed(() => this.readonly() || this.disabled());

  private quill: Quill | null = null;
  /** Último valor recebido antes de o Quill existir. */
  private pendingValue: string | null = null;
  /** Última seleção conhecida: menus e popovers da barra tiram o foco do texto. */
  private lastRange: Range | null = null;
  private lastVariableKeys = '';
  /** O import do Quill é assíncrono: a tela pode fechar antes de ele chegar. */
  private destroyed = false;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  private readonly textChangeHandler = (): void => this.handleTextChange();
  private readonly selectionChangeHandler = (range: Range | null): void => {
    if (range) {
      this.lastRange = range;
      this.refreshFormats(range);
    } else {
      this.onTouched();
    }
  };

  constructor() {
    afterNextRender(() => {
      void this.initQuill();
    });

    // Reaplica o bloqueio quando `readonly`/`disabled` mudam depois da criação do Quill.
    effect(() => {
      const locked = this.isLocked();
      if (this.ready()) this.quill?.enable(!locked);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.quill?.off('text-change', this.textChangeHandler);
    this.quill?.off('selection-change', this.selectionChangeHandler);
    this.quill = null;
  }

  // --- ControlValueAccessor ---

  writeValue(value: string | null): void {
    if (!this.quill) {
      this.pendingValue = value ?? '';
      return;
    }
    this.setHtml(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    this.syncEnabled();
  }

  // --- Barra de ferramentas ---

  onCommand(command: RichTextCommand): void {
    const quill = this.quill;
    if (!quill || this.isLocked()) return;

    // Devolve o foco e a seleção que o menu/popover tirou.
    const range = this.lastRange ?? { index: Math.max(0, quill.getLength() - 1), length: 0 };
    quill.focus({ preventScroll: true });
    quill.setSelection(range.index, range.length, 'silent');

    applyRichTextCommand(quill, command, range);

    const current = quill.getSelection();
    if (current) {
      this.lastRange = current;
      this.refreshFormats(current);
    }
  }

  toggleFullscreen(): void {
    this.fullscreen.update(value => !value);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.fullscreen()) this.fullscreen.set(false);
  }

  // --- Quill ---

  private async initQuill(): Promise<void> {
    const { default: QuillClass } = await import('quill');
    if (this.destroyed || !this.editorHost) return;

    const quill = new QuillClass(this.editorHost.nativeElement, {
      theme: 'snow',
      placeholder: this.placeholder(),
      // A barra é a nossa; o módulo `table` do Quill 2 já vem registrado no pacote completo.
      modules: { toolbar: false, table: true, history: { delay: 800, maxStack: 200 } },
    });

    this.quill = quill;
    this.setHtml(this.pendingValue ?? '');
    this.pendingValue = null;

    quill.on('text-change', this.textChangeHandler);
    quill.on('selection-change', this.selectionChangeHandler);

    this.syncEnabled();
    this.ready.set(true);
  }

  /**
   * Escreve o HTML sem disparar `text-change` (o formulário não fica "sujo" só por carregar) e
   * zera o histórico, para o desfazer não voltar ao editor vazio. Usa `clipboard.convert` +
   * `setContents` em vez do `dangerouslyPasteHTML(html)`, que posiciona o cursor e rouba o foco.
   */
  private setHtml(html: string): void {
    const quill = this.quill;
    if (!quill) return;

    const delta = quill.clipboard.convert({ html, text: '' });
    quill.setContents(delta, 'silent');
    quill.history.clear();
    this.emitVariableKeys();
  }

  private handleTextChange(): void {
    this.onChange(this.readHtml());
    this.emitVariableKeys();

    const range = this.quill?.getSelection();
    if (range) this.refreshFormats(range);
  }

  /**
   * HTML do conteúdo. Editor vazio vira `''` (e não `<p><br></p>`), e sai o `span.ql-ui`, que é só
   * o marcador visual das listas dentro do editor.
   */
  private readHtml(): string {
    const quill = this.quill;
    if (!quill || quill.getLength() <= 1) return '';
    return quill.root.innerHTML.replace(/<span class="ql-ui"[^>]*><\/span>/g, '');
  }

  private refreshFormats(range: Range): void {
    this.formats.set(this.quill?.getFormat(range) ?? {});
  }

  private emitVariableKeys(): void {
    const keys = findVariableKeys(this.quill?.getText() ?? '');
    const signature = keys.join('|');
    if (signature === this.lastVariableKeys) return;
    this.lastVariableKeys = signature;
    this.unfilledVariablesChange.emit(keys);
  }

  private syncEnabled(): void {
    this.quill?.enable(!this.isLocked());
  }
}
